import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, ExternalLink, Plus, Trash2 } from "lucide-react";

const EMPTY = { title: "", description: "", day_of_week: "Friday", scheduled_at: "", duration_minutes: 60, join_url: "" };

const toLocalInput = (iso) => (iso || "").slice(0, 16);

export const LiveClassManager = () => {
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/yoga/admin/live-sessions").then(({ data }) => setSessions(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.scheduled_at) return toast.error("A title and a date & time are needed");
    const body = { ...form, duration_minutes: parseInt(form.duration_minutes, 10) || 60 };
    try {
      if (editingId) await api.put(`/yoga/admin/live-sessions/${editingId}`, body);
      else await api.post("/yoga/admin/live-sessions", body);
      toast.success(editingId ? "Class updated" : "Class scheduled");
      setForm(EMPTY); setEditingId(null); setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this live class?")) return;
    await api.delete(`/yoga/admin/live-sessions/${id}`);
    load();
    toast.success("Removed");
  };

  return (
    <div data-testid="live-class-manager">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl">Live Classes</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Your Friday and Saturday sessions. Members see the next one on their yoga dashboard with the join link.
          </p>
        </div>
        <Button
          onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }}
          className="bg-primary text-primary-foreground btn-pill"
          data-testid="live-add"
        >
          <Plus size={14} className="mr-1"/> Schedule Class
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.map((ls) => (
          <Card key={ls.id} className="border-border" data-testid={`live-card-${ls.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Badge variant="outline" className="text-[10px] mb-2">{ls.day_of_week || "Live"}</Badge>
                  <div className="font-display text-xl leading-tight">{ls.title}</div>
                </div>
                <button onClick={() => remove(ls.id)} className="text-destructive shrink-0" data-testid={`live-del-${ls.id}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
              <div className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                <CalendarClock size={12}/>
                {ls.scheduled_at ? new Date(ls.scheduled_at).toLocaleString() : "No time set"} · {ls.duration_minutes} min
              </div>
              {ls.description && <p className="text-xs text-muted-foreground mt-2">{ls.description}</p>}
              <div className="flex items-center gap-3 mt-4">
                {ls.join_url && (
                  <a href={ls.join_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`live-open-${ls.id}`}>
                    <ExternalLink size={12}/> Join link
                  </a>
                )}
                <button
                  onClick={() => {
                    setForm({ ...EMPTY, ...ls, scheduled_at: toLocalInput(ls.scheduled_at) });
                    setEditingId(ls.id);
                    setOpen(true);
                  }}
                  className="text-xs text-primary hover:underline"
                  data-testid={`live-edit-${ls.id}`}
                >
                  Edit
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
        {sessions.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="live-empty">
            No live classes scheduled yet.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit live class" : "Schedule a live class"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} placeholder="Friday Pranayama" data-testid="live-title"/></div>
            <div><Label>What members will practise</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} data-testid="live-desc"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Day</Label><Input value={form.day_of_week} onChange={(e)=>setForm({...form,day_of_week:e.target.value})} placeholder="Friday" data-testid="live-day"/></div>
              <div><Label>Minutes</Label><Input type="number" value={form.duration_minutes} onChange={(e)=>setForm({...form,duration_minutes:e.target.value})} data-testid="live-duration"/></div>
            </div>
            <div><Label>Date &amp; time</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e)=>setForm({...form,scheduled_at:e.target.value})} data-testid="live-when"/></div>
            <div><Label>Join link (Zoom / Meet)</Label><Input value={form.join_url} onChange={(e)=>setForm({...form,join_url:e.target.value})} placeholder="https://meet.google.com/…" data-testid="live-url"/></div>
          </div>
          <Button onClick={save} className="bg-primary text-primary-foreground mt-4" data-testid="live-save">
            {editingId ? "Update class" : "Schedule class"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveClassManager;
